/**
 * BankIcon Component
 * 
 * Displays a modern, stylized bank icon with the bank's brand colors,
 * a subtle building icon overlay, and the bank's initials.
 * If a custom logo is available, it displays that instead.
 */

import { getBankColors } from '../../utils/bankColors';

interface BankIconProps {
  /** Bank name to determine colors and initials */
  bankName: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

// Building icon as inline SVG - larger and more prominent
const BuildingIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth={1.2} 
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" 
    />
  </svg>
);

const sizeClasses = {
  sm: {
    container: 'w-8 h-8',
    initials: 'text-[9px]',
    building: 'w-7 h-7',
  },
  md: {
    container: 'w-10 h-10',
    initials: 'text-[10px]',
    building: 'w-9 h-9',
  },
  lg: {
    container: 'w-12 h-12',
    initials: 'text-xs',
    building: 'w-11 h-11',
  },
};

export function BankIcon({ bankName, size = 'md', className = '' }: BankIconProps) {
  const bankColors = getBankColors(bankName);
  const sizeClass = sizeClasses[size];

  // If a custom logo exists, display it
  if (bankColors.logo) {
    return (
      <div className={`${sizeClass.container} rounded-xl overflow-hidden shadow-sm ${className}`}>
        <img 
          src={`/banks/${bankColors.logo}`} 
          alt={bankName}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  // Otherwise, display the generated icon with colors and initials
  return (
    <div
      className={`${sizeClass.container} rounded-xl flex items-center justify-center relative overflow-hidden ${className}`}
      style={{
        background: `linear-gradient(145deg, ${bankColors.primary}90, ${bankColors.secondary}90)`,
        boxShadow: `0 2px 8px -1px ${bankColors.primary}30`,
      }}
    >
      {/* Subtle inner glow at top for 3D effect */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.25) 0%, transparent 40%)',
        }}
      />
      
      {/* Building icon - large and prominent */}
      <BuildingIcon 
        className={`${sizeClass.building} absolute opacity-30`}
        style={{ color: bankColors.text }}
      />
      
      {/* Initials */}
      {bankColors.initials && (
        <span 
          className={`${sizeClass.initials} font-bold relative z-10 tracking-tight`}
          style={{ 
            color: bankColors.text,
            textShadow: '0 1px 2px rgba(0,0,0,0.15)',
          }}
        >
          {bankColors.initials}
        </span>
      )}
    </div>
  );
}

export default BankIcon;
